import { z } from 'zod';

/** Vorgangsarten - entsprechen den 5 Kacheln der Tablet-App. */
export const ProtocolType = {
  /** Nutzervereinbarung / Nutzerwechsel (Stammdaten-Formular, XLSX-Vorlage) */
  NUTZERWECHSEL: 'NUTZERWECHSEL',
  /** Uebergabeprotokoll: Fahrzeug geht AN den Nutzer */
  UEBERGABE: 'UEBERGABE',
  /** Rueckgabecheckliste: Fahrzeug kommt VOM Nutzer zurueck ("Uebernahme" durch den FPK) */
  RUECKGABE: 'RUECKGABE',
  /** Poolfahrzeug: Fahrer uebernimmt (Anmeldung) */
  POOL_AUSGABE: 'POOL_AUSGABE',
  /** Poolfahrzeug: Fahrer gibt zurueck (Abmeldung) */
  POOL_RUECKNAHME: 'POOL_RUECKNAHME',
  /** Wiederkehrende Zustandsdokumentation ("Check-In") */
  ZUSTANDSCHECK: 'ZUSTANDSCHECK',
} as const;
export type ProtocolType = (typeof ProtocolType)[keyof typeof ProtocolType];
export const protocolTypeSchema = z.nativeEnum(ProtocolType);

export const PROTOCOL_TYPE_LABEL: Record<ProtocolType, string> = {
  NUTZERWECHSEL: 'Nutzerwechsel',
  UEBERGABE: 'Übergabe',
  RUECKGABE: 'Übernahme / Rückgabe',
  POOL_AUSGABE: 'Pool – Übernahme',
  POOL_RUECKNAHME: 'Pool – Rückgabe',
  ZUSTANDSCHECK: 'Check-In (Zustand)',
};

/** Kurzform fuer Belegnummern: AP-UEB-2026-000123 */
export const PROTOCOL_TYPE_CODE: Record<ProtocolType, string> = {
  NUTZERWECHSEL: 'NW',
  UEBERGABE: 'UEB',
  RUECKGABE: 'RG',
  POOL_AUSGABE: 'PA',
  POOL_RUECKNAHME: 'PR',
  ZUSTANDSCHECK: 'ZC',
};

export const ProtocolStatus = {
  ENTWURF: 'ENTWURF',
  ABGESCHLOSSEN: 'ABGESCHLOSSEN',
  VERSENDET: 'VERSENDET',
  VERSANDFEHLER: 'VERSANDFEHLER',
} as const;
export type ProtocolStatus = (typeof ProtocolStatus)[keyof typeof ProtocolStatus];
export const protocolStatusSchema = z.nativeEnum(ProtocolStatus);

export const PROTOCOL_STATUS_LABEL: Record<ProtocolStatus, string> = {
  ENTWURF: 'Entwurf',
  ABGESCHLOSSEN: 'Abgeschlossen',
  VERSENDET: 'Versendet',
  VERSANDFEHLER: 'Versandfehler',
};

/** Fahrzeugklasse - steuert, welches Schadens-Schema und welche Fotoserie gilt. */
export const VehicleCategory = { PKW: 'PKW', LCV: 'LCV' } as const;
export type VehicleCategory = (typeof VehicleCategory)[keyof typeof VehicleCategory];
export const vehicleCategorySchema = z.nativeEnum(VehicleCategory);

export const VEHICLE_CATEGORY_LABEL: Record<VehicleCategory, string> = {
  PKW: 'PKW',
  LCV: 'Nutzfahrzeug (LCV)',
};

/** Nutzungsart - trennt Dienstwagen (Nutzerwechsel-Prozess) von Poolfahrzeugen. */
export const VehicleUsage = { DIENSTWAGEN: 'DIENSTWAGEN', POOL: 'POOL' } as const;
export type VehicleUsage = (typeof VehicleUsage)[keyof typeof VehicleUsage];
export const vehicleUsageSchema = z.nativeEnum(VehicleUsage);

export const VEHICLE_USAGE_LABEL: Record<VehicleUsage, string> = {
  DIENSTWAGEN: 'Dienstfahrzeug',
  POOL: 'Poolfahrzeug',
};

export const VehicleStatus = {
  VERFUEGBAR: 'VERFUEGBAR',
  AUSGEGEBEN: 'AUSGEGEBEN',
  WERKSTATT: 'WERKSTATT',
  AUSGEMUSTERT: 'AUSGEMUSTERT',
} as const;
export type VehicleStatus = (typeof VehicleStatus)[keyof typeof VehicleStatus];
export const vehicleStatusSchema = z.nativeEnum(VehicleStatus);

export const VEHICLE_STATUS_LABEL: Record<VehicleStatus, string> = {
  VERFUEGBAR: 'Verfügbar',
  AUSGEGEBEN: 'Ausgegeben',
  WERKSTATT: 'In Werkstatt',
  AUSGEMUSTERT: 'Ausgemustert',
};

export const FuelType = {
  BENZIN: 'BENZIN',
  DIESEL: 'DIESEL',
  ELEKTRO: 'ELEKTRO',
  HYBRID: 'HYBRID',
  PLUGIN_HYBRID: 'PLUGIN_HYBRID',
  SONSTIGES: 'SONSTIGES',
} as const;
export type FuelType = (typeof FuelType)[keyof typeof FuelType];
export const fuelTypeSchema = z.nativeEnum(FuelType);

export const FUEL_TYPE_LABEL: Record<FuelType, string> = {
  BENZIN: 'Benzin',
  DIESEL: 'Diesel',
  ELEKTRO: 'Elektro',
  HYBRID: 'Hybrid',
  PLUGIN_HYBRID: 'Plug-in-Hybrid',
  SONSTIGES: 'Sonstiges',
};

/** E-Fahrzeuge brauchen zusaetzliche Checklistenpunkte (Ladekabel). */
export const isElectric = (f: FuelType): boolean =>
  f === FuelType.ELEKTRO || f === FuelType.PLUGIN_HYBRID || f === FuelType.HYBRID;

export const UserRole = {
  ADMIN: 'ADMIN',
  KOORDINATOR: 'KOORDINATOR',
  POOLVERANTWORTLICHER: 'POOLVERANTWORTLICHER',
} as const;
export type UserRole = (typeof UserRole)[keyof typeof UserRole];
export const userRoleSchema = z.nativeEnum(UserRole);

export const USER_ROLE_LABEL: Record<UserRole, string> = {
  ADMIN: 'Administrator',
  KOORDINATOR: 'Fuhrparkkoordinator',
  POOLVERANTWORTLICHER: 'Poolfahrzeug-Verantwortlicher',
};

/** Welche Vorgangsart darf welche Rolle anlegen? */
export const ROLE_ALLOWED_TYPES: Record<UserRole, ProtocolType[]> = {
  ADMIN: Object.values(ProtocolType),
  KOORDINATOR: Object.values(ProtocolType),
  POOLVERANTWORTLICHER: [
    ProtocolType.POOL_AUSGABE,
    ProtocolType.POOL_RUECKNAHME,
    ProtocolType.ZUSTANDSCHECK,
  ],
};

export const SignatureRole = {
  FPK: 'FPK',
  NUTZER: 'NUTZER',
  BISHERIGER_NUTZER: 'BISHERIGER_NUTZER',
  FAHRER: 'FAHRER',
} as const;
export type SignatureRole = (typeof SignatureRole)[keyof typeof SignatureRole];
export const signatureRoleSchema = z.nativeEnum(SignatureRole);

export const SIGNATURE_ROLE_LABEL: Record<SignatureRole, string> = {
  FPK: 'Fuhrparkkoordinator',
  NUTZER: 'Nutzer*in',
  BISHERIGER_NUTZER: 'Bisherige*r Nutzer*in',
  FAHRER: 'Fahrer*in',
};

export const MailStatus = {
  WARTEND: 'WARTEND',
  GESENDET: 'GESENDET',
  FEHLER: 'FEHLER',
} as const;
export type MailStatus = (typeof MailStatus)[keyof typeof MailStatus];
